// Import the type from the wire-message module so this file (and its
// unit tests) do not pull the React Native / `expo-image` graph that the
// `@visioni/a3s-media/app` barrel re-exports.
import type { TimelapseVideoItem } from "@visioni/a3s-media/client-messages";
import type { TimelapseExportState } from "../types";

export interface DeriveExportStateInput {
  readonly job: TimelapseVideoItem | null;
  readonly isRequesting: boolean;
  readonly hasRequested: boolean;
}

/**
 * Pure projection from job + UI flags to the discriminated UX state.
 *
 * Lives in its own module so unit tests can exercise it without dragging in
 * the React Native / `expo-image` import graph that the surrounding hook
 * pulls through `@visioni/a3s-media/app`.
 *
 * The two app-only states (`idle`, `requesting`) are documented in
 * `TimelapseExportState` — they exist because the server's lifecycle starts
 * at `queued` and there's a window between the user clicking and the POST
 * resolving where neither client nor server has a job to report.
 */
export function deriveExportState({ job, isRequesting, hasRequested }: DeriveExportStateInput): TimelapseExportState {
  if (isRequesting) return { kind: "requesting" };
  if (!job) return hasRequested ? { kind: "queued" } : { kind: "idle" };

  switch (job.status) {
    case "queued":
      return { kind: "queued" };
    case "processing":
      return { kind: "processing" };
    case "ready":
      // The status response only attaches the signed URL when the job is
      // ready; treat a missing URL as "still finalising" so the UI keeps
      // polling instead of crashing on a half-populated payload.
      if (!job.video) return { kind: "processing" };
      return { kind: "ready", downloadUrl: job.video.url, expiresAt: job.video.expiresAt };
    case "failed":
      return { kind: "failed", reason: job.failureReason ?? "Render failed" };
  }
}
